/**
 * Operator-side branding editor for a single tenant
 * (Welle 4.6 NS-2). Mounted as a section inside TenantDetailPage.
 *
 * Inputs: displayName, primaryColor (color picker + hex text),
 * logoUrl (text), supportEmail (text). A live preview card
 * mirrors how the tenant-admin portal header will render — the
 * tenant's own users will see exactly that header on their
 * portal subdomain.
 *
 * Validation is client-side (immediate feedback) but the server
 * is authoritative: the same /^#[0-9a-fA-F]{6}$/ hex check, the
 * same https-only logoUrl check, the same mailto-safe email
 * shape. Anything the SPA accepts but the server refuses lands
 * here as a 400 with the server's error message.
 *
 * Save behavior: PUT /api/tenants/:slug/branding. On success the
 * "saved at" stamp updates from the server-returned row. The
 * audit row (`tenant.branding.updated`) is written server-side.
 */
import { useEffect, useState } from 'react';
import api, { TenantBranding } from '../services/api';
import { useBrand } from '../contexts/BrandContext';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
// SecTepe-Markenkoralle (coral-600) — WCAG-AA-tauglich als Header-Hintergrund
// mit weisser Schrift. Default-Markenfarbe neuer Tenants; Tenants ueberschreiben.
const DEFAULT_PRIMARY_COLOR = '#B83E39';

function isValidColor(v: string): boolean {
  return HEX_COLOR_RE.test(v);
}

function isValidLogoUrl(v: string): boolean {
  if (v === '') return true;
  try {
    return new URL(v).protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidEmail(v: string): boolean {
  if (v === '') return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) && v.length <= 254;
}

function isValidDisplayName(v: string): boolean {
  return v.trim().length > 0 && v.length <= 120;
}

export function BrandingSection({ slug, isReadOnly }: Props) {
  const { brand, domain } = useBrand();
  const [loaded, setLoaded] = useState(false);
  const [row, setRow] = useState<TenantBranding | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [logoUrl, setLogoUrl] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<TenantBranding>(`/tenants/${slug}/branding`).then((r) => {
      if (cancelled) return;
      setRow(r.data);
      setDisplayName(r.data.displayName);
      setPrimaryColor(r.data.primaryColor);
      setLogoUrl(r.data.logoUrl ?? '');
      setSupportEmail(r.data.supportEmail ?? '');
      setSavedAt(r.data.isDefault ? null : r.data.updatedAt);
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) {
        setErr('Branding konnte nicht geladen werden');
        setLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, [slug]);

  const displayNameValid = isValidDisplayName(displayName);
  const colorValid = isValidColor(primaryColor);
  const logoValid = isValidLogoUrl(logoUrl);
  const emailValid = isValidEmail(supportEmail);
  const allValid = displayNameValid && colorValid && logoValid && emailValid;

  const save = async () => {
    if (!allValid || isReadOnly) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.put<TenantBranding>(`/tenants/${slug}/branding`, {
        displayName: displayName.trim(),
        primaryColor: primaryColor.toLowerCase(),
        logoUrl: logoUrl.trim() === '' ? null : logoUrl.trim(),
        supportEmail: supportEmail.trim() === '' ? null : supportEmail.trim(),
      });
      setRow(r.data);
      setSavedAt(r.data.updatedAt);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setErr(msg ?? 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <p className="text-slate-500 text-sm">Lade Branding…</p>;

  // The live preview applies the FORM color (not the saved one) so
  // the operator sees the in-flight change before clicking save.
  // Fall back to the default brand colour if the form color is
  // currently invalid; that way the preview never renders an empty
  // background while the operator is typing.
  const previewColor = colorValid ? primaryColor : DEFAULT_PRIMARY_COLOR;
  const previewDisplayName = displayNameValid ? displayName : `${brand.productName} Tenant Portal`;
  const previewLogo = logoValid && logoUrl !== '' ? logoUrl : null;
  const previewEmail = emailValid && supportEmail !== '' ? supportEmail : null;

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">Branding (Tenant-Portal)</h3>
      <p className="text-xs text-slate-500 mb-3">
        Anzeigename, Farbe, Logo und Support-Adresse für das Self-Service-Portal des Tenants
        (<span className="font-mono">portal-{slug}.{domain}</span>).
        Operator-only — Tenants können diese Werte nur lesen.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {row?.isDefault && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          Noch keine angepasste Branding-Konfiguration. Es werden Standardwerte angezeigt.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-3">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Anzeigename</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isReadOnly}
              maxLength={120}
              className="w-full px-2 py-1 rounded border"
            />
            {!displayNameValid && (
              <p className="text-xs text-red-600 mt-1">1 bis 120 Zeichen erforderlich.</p>
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Primärfarbe</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={colorValid ? primaryColor : DEFAULT_PRIMARY_COLOR}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={isReadOnly}
                className="h-9 w-12 rounded border"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={isReadOnly}
                placeholder="#1a64ed"
                className="flex-1 px-2 py-1 rounded border font-mono"
              />
            </div>
            {!colorValid && (
              <p className="text-xs text-red-600 mt-1">
                Hex-Farbcode erwartet (z.B. <span className="font-mono">#1a64ed</span>).
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Logo-URL (https, optional)</label>
            <input
              type="text"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              disabled={isReadOnly}
              placeholder="https://cdn.acme.example/logo.png"
              className="w-full px-2 py-1 rounded border font-mono text-sm"
            />
            {!logoValid && (
              <p className="text-xs text-red-600 mt-1">
                Nur <span className="font-mono">https://</span>-URLs sind zulässig.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Support-Email (optional)</label>
            <input
              type="text"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              disabled={isReadOnly}
              placeholder="support@acme.example"
              className="w-full px-2 py-1 rounded border"
            />
            {!emailValid && (
              <p className="text-xs text-red-600 mt-1">Bitte eine gültige Email-Adresse eingeben.</p>
            )}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={save}
              disabled={busy || !allValid || isReadOnly}
              className="btn-primary disabled:opacity-50"
            >
              Speichern
            </button>
            {savedAt && (
              <span className="text-xs text-slate-500">
                Zuletzt gespeichert: {new Date(savedAt).toLocaleString()}
              </span>
            )}
          </div>
          {isReadOnly && (
            <p className="text-xs text-slate-400">Read-only-Operator; Branding kann nicht bearbeitet werden.</p>
          )}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-600">Vorschau (Tenant-Portal-Header)</h4>
          <div className="rounded shadow overflow-hidden border">
            <div className="px-4 py-3 flex items-center justify-between text-fg-on-dark bg-surface-invert">
              <div>
                <div className="text-base font-semibold">{previewDisplayName}</div>
                <div className="text-xs opacity-80 font-mono">{slug}</div>
                {previewLogo && (
                  <div className="text-xs opacity-80 mt-1">
                    Logo: <span className="font-mono">{previewLogo}</span>
                  </div>
                )}
                <div className="text-xs opacity-80 mt-1">
                  Brand-Farbe: <span className="font-mono">{previewColor}</span>
                </div>
              </div>
              <div className="text-xs opacity-80">Plan · Logout</div>
            </div>
            <div className="bg-slate-50 px-4 py-3 text-xs text-slate-500 border-t flex justify-between">
              <span>Footer (Tenant-Portal)</span>
              {previewEmail && (
                <span className="text-slate-600">
                  Support: {previewEmail}
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Vorschau zeigt die Werte als Text; die volle Darstellung
            (dynamische Brand-Farbe, eingebettetes Logo, mailto-Link)
            kommt mit einem Folge-PR der die CodeQL-Validatoren
            referenziert. Daten werden bereits persistiert.
          </p>
        </div>
      </div>
    </section>
  );
}
